import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAllBlogs,
  getBlogById,
  createBlog,
  updateBlog,
  publishBlog,
  toggleBlogPublish,
  saveBlogAsDraft,
  uploadBlogCoverImage,
  deleteBlog,
} from "../api/blogs";
import { BlogRequest } from "../types/blogs";

export function useBlogs() {
  const queryClient = useQueryClient();

  const useBlogsQuery = (page: number, size: number, search?: string) =>
    useQuery({
      queryKey: ["blogs", page, size, search],
      queryFn: () => getAllBlogs(page, size, search).then((res) => res.data),
      keepPreviousData: true,
    });

  const useBlogQuery = (id: number, enabled = true) =>
    useQuery({
      queryKey: ["blog", id],
      queryFn: () => getBlogById(id).then((res) => res.data),
      enabled: enabled && !!id,
    });

  const createBlogMutation = useMutation({
    mutationFn: (data: BlogRequest) => createBlog(data).then((res) => res.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["blogs"] });
    },
  });

  const updateBlogMutation = useMutation({
    mutationFn: (args: { id: number; data: BlogRequest }) =>
      updateBlog(args.id, args.data).then((res) => res.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["blogs"] });
      void queryClient.invalidateQueries({ queryKey: ["blog"] });
    },
  });

  const publishBlogMutation = useMutation({
    mutationFn: (id: number) => publishBlog(id).then((res) => res.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["blogs"] });
      void queryClient.invalidateQueries({ queryKey: ["blog"] });
    },
  });

  const toggleBlogPublishMutation = useMutation({
    mutationFn: (id: number) => toggleBlogPublish(id).then((res) => res.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["blogs"] });
      void queryClient.invalidateQueries({ queryKey: ["blog"] });
    },
  });

  const saveBlogAsDraftMutation = useMutation({
    mutationFn: (id: number) => saveBlogAsDraft(id).then((res) => res.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["blogs"] });
      void queryClient.invalidateQueries({ queryKey: ["blog"] });
    },
  });

  const uploadBlogCoverImageMutation = useMutation({
    mutationFn: (file: File) => uploadBlogCoverImage(file).then((res) => res.data),
  });

  const deleteBlogMutation = useMutation({
    mutationFn: (id: number) => deleteBlog(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["blogs"] });
    },
  });

  return {
    useBlogsQuery,
    useBlogQuery,
    createBlogMutation,
    updateBlogMutation,
    publishBlogMutation,
    toggleBlogPublishMutation,
    saveBlogAsDraftMutation,
    uploadBlogCoverImageMutation,
    deleteBlogMutation,
  };
}
